import { Controller } from 'stimulus';
import { openDB, putItem, getItem, deleteItem, dropDB } from '@/js/shared/indexed-db';

export default class extends Controller {
  static values = {
    dbName: String,
    dbStore: String,
    deleteOnDisconnect: Boolean,
    dispatchConnected: Boolean,
  };

  #dbInstance;
  #connectedDispatched = false;

  connect() {
    if (!this.dispatchConnectedValue) {
      return;
    }

    if (this.#connectedDispatched) {
      return;
    }

    this.#connectedDispatched = true;

    setTimeout(() => this.dispatch('connected'));
  }

  async disconnect() {
    if (!this.#dbInstance) {
      return;
    }

    await this.#dbInstance.close();

    if (this.deleteOnDisconnectValue) {
      await this.deleteDB();
    }
  }

  async putItems(event) {
    const items = event.detail.extraPayload;
    const keys = await Promise.all(
      items.map(async ({ name, value }) => {
        const uniqueKey = `${name}-${crypto.randomUUID()}`;
        await this.#putItem(uniqueKey, value);
        return { name: 'indexed_db_keys[]', value: uniqueKey };
      })
    );

    this.dispatch('stored', { detail: { extraPayload: keys } });
  }

  async #putItem(key, value) {
    await putItem(await this.#openDB(), this.dbStoreValue, key, value);
  }

  async getItemsFromParams(event) {
    const { indexedDbKeys } = event.params;

    const extraPayload = await Promise.all(
      indexedDbKeys.map(async (key) => {
        const item = await this.#getItem(key);
        if (item) {
          this.dispatch('item-fetched', { detail: { item } });
        }
        return { name: 'items[]', value: item };
      })
    );

    this.dispatch('fetched', { detail: { extraPayload } });
  }

  async #getItem(key) {
    return await getItem(await this.#openDB(), this.dbStoreValue, key);
  }

  async deleteItemsFromParams(event) {
    const { indexedDbKeys } = event.params;
    await Promise.all(
      indexedDbKeys.map(async (key) => {
        return await this.#deleteItem(key);
      })
    );
  }

  async #deleteItem(key) {
    await deleteItem(await this.#openDB(), this.dbStoreValue, key);
  }

  async deleteDB() {
    await dropDB(this.dbNameValue);
    this.#dbInstance = null;
  }

  async #openDB() {
    if (this.#dbInstance) {
      return this.#dbInstance;
    }

    this.#dbInstance = await openDB(this.dbNameValue, this.dbStoreValue);

    return this.#dbInstance;
  }
}
